import { isAfter, isBefore, parseISO } from 'date-fns';
import type { LiveNotification } from '@debatdirect/common/types/debatdirect-typings';

import { inject, injectable } from '../modules/container';
import type { AppDebate, AppAgendaDebate } from '../../types/debate';

import StorageService from './StorageService';

const LOCAL_STORAGE_KEY = 'sn';

@injectable()
export default class NotificationsService {
  /**
   * List of all notification ids seen by the user
   * @type {Array}
   */
  protected seenNotifications: unknown[] = [];

  constructor(@inject(StorageService) readonly storage: StorageService) {}

  /**
   * Returns true if the given notification should be visible for the current user
   */
  protected notificationShouldBeVisible = (
    notification: LiveNotification,
    debate: AppDebate | undefined,
    embeddedLocationId: string | undefined,
    debates: AppAgendaDebate[],
  ) => {
    const startsAt = notification.startsAt;
    const endsAt = notification.endsAt;

    if (startsAt && !isAfter(new Date(), parseISO(startsAt))) {
      return false;
    }

    if (endsAt && !isBefore(new Date(), parseISO(endsAt))) {
      return false;
    }

    /**
     * The user has already seen this notification
     */
    if (this.seenNotifications.indexOf(notification.id) > -1) {
      return false;
    }

    const debateId = notification.debateId;

    // For embedded: we check if the locationId matches the notification debate locationId
    if (debateId && embeddedLocationId) {
      if (!debate || debate.locationId !== embeddedLocationId) {
        return false;
      }
    }

    const meetingId = notification.meetingId;

    // For embedded: if no debateId is given, check if the notification is for a whole meeting
    if (!debateId && meetingId && embeddedLocationId) {
      // @TODO we need a locationId in the event
      console.warn('Skipping the embedded live notification check');
      // const debates = queryClient.getQueryData<AppAgenda>(['agenda', format(new Date(), 'yyyy-MM-dd')])?.debates || [];
      // const meetingDebate = debates.find((current) => current.meetingId === meetingId);
      // if (!debate || debate.locationId !== embeddedLocationId) {
      //   return false;
      // }
    }

    // If not on the detail screen: filter non-plenary Schorsing notifications
    if (!debate && debateId && !embeddedLocationId && notification.type === 'Schorsing') {
      // @TODO this can be optimized when there is a locationId in the event
      const targetDebate = debates.find((current) => current.id === debateId);

      if (targetDebate?.locationId !== 'plenaire-zaal') {
        return false;
      }
    }

    // we got a debate, so we should be on a debate details screen...
    // we only show notifications if the debate id matches or the meeting id
    if (debate) {
      if (!debateId && meetingId) {
        return meetingId === debate.meetingId;
      }

      return !debateId || debateId === debate.id;
    }

    return true;
  };

  /**
   * Check if we should display notifications
   */
  async getVisibleNotifications(
    notifications: LiveNotification[],
    debate: AppDebate | undefined,
    embeddedLocationId: string | undefined,
    debates: AppAgendaDebate[],
  ) {
    this.seenNotifications = (await this.storage.getItem(LOCAL_STORAGE_KEY, true)) || [];

    if (!notifications) {
      return [];
    }

    // loop through all notifications and check if we need to make one or more visible.
    return notifications.filter((notification) => {
      // only allow certain notification types
      if (!['Schorsing', 'Incident', 'Melding'].includes(notification.type)) {
        return false;
      }

      return this.notificationShouldBeVisible(notification, debate, embeddedLocationId, debates);
    });
  }

  /**
   * Mark notification as seen
   */
  async markAsSeen(notificationId: string) {
    // add to seen notifications
    if (this.seenNotifications.indexOf(notificationId) === -1) {
      this.seenNotifications.unshift(notificationId);
    }

    // save up to 20 notifications in the localStorage
    await this.storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.seenNotifications.slice(0, 20)));
  }
}
